import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import VAHeaderNav from '../../components/navigation/VAHeaderNav';
import Footer from '../../components/layout/Footer';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';

const HowItWorksPage = () => {
  return (
    <>
      <Helmet>
        <title>How It Works | VA Sprouts</title>
        <meta name="description" content="Learn how VA Sprouts connects virtual assistants with employers through our AI-powered matching system." />
      </Helmet>

      <VAHeaderNav />

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary-50 to-blue-50 pt-20 pb-16 md:pt-28 md:pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">How VA Sprouts Works</h1>
            <p className="text-xl text-gray-700 mb-8 max-w-3xl mx-auto">
              Our platform makes it easy to connect skilled virtual assistants with employers looking for quality talent.
            </p>
          </div>
        </div>
      </section>

      {/* Process Overview */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">The Process</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our streamlined process helps both VAs and employers find their perfect match
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-16">
            {/* For VAs */}
            <div>
              <div className="bg-blue-50 rounded-xl p-6 mb-8">
                <h3 className="text-2xl font-bold text-blue-700 mb-4">For Virtual Assistants</h3>
                <p className="text-gray-700 mb-4">
                  Join our platform to showcase your skills and connect with quality employers.
                </p>
              </div>

              <ol className="space-y-8">
                <li className="flex">
                  <div className="mr-4">
                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 text-blue-700 font-bold">
                      1
                    </div>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-2">Create Your Profile</h4>
                    <p className="text-gray-600">
                      Sign up and build a comprehensive profile showcasing your skills, experience, and portfolio. The more detailed your profile, the better your matches will be.
                    </p>
                  </div>
                </li>

                <li className="flex">
                  <div className="mr-4">
                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 text-blue-700 font-bold">
                      2
                    </div>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-2">Verify Your Skills</h4>
                    <p className="text-gray-600">
                      Take skill assessments to verify your expertise. Verified skills significantly increase your chances of getting hired and help you stand out from the crowd.
                    </p>
                  </div>
                </li>

                <li className="flex">
                  <div className="mr-4">
                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 text-blue-700 font-bold">
                      3
                    </div>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-2">Browse & Apply for Jobs</h4>
                    <p className="text-gray-600">
                      Browse job listings or receive AI-matched recommendations based on your skills and preferences. Apply to positions that interest you with personalized applications.
                    </p>
                  </div>
                </li>

                <li className="flex">
                  <div className="mr-4">
                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 text-blue-700 font-bold">
                      4
                    </div>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-2">Interview & Get Hired</h4>
                    <p className="text-gray-600">
                      Connect with potential employers through our secure messaging system, schedule interviews, and get hired for projects that match your skills and career goals.
                    </p>
                  </div>
                </li>
              </ol>

              <div className="mt-8">
                <Link to="/auth/register?role=va">
                  <Button variant="primary" size="lg" className="w-full">
                    Join as a VA
                  </Button>
                </Link>
              </div>
            </div>

            {/* For Employers */}
            <div>
              <div className="bg-green-50 rounded-xl p-6 mb-8">
                <h3 className="text-2xl font-bold text-green-700 mb-4">For Employers</h3>
                <p className="text-gray-700 mb-4">
                  Find skilled virtual assistants who match your specific needs and business requirements.
                </p>
              </div>

              <ol className="space-y-8">
                <li className="flex">
                  <div className="mr-4">
                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-green-100 text-green-700 font-bold">
                      1
                    </div>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-2">Create Your Account</h4>
                    <p className="text-gray-600">
                      Sign up as an employer and complete your company profile with details about your business, team, and the types of virtual assistants you're looking for.
                    </p>
                  </div>
                </li>

                <li className="flex">
                  <div className="mr-4">
                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-green-100 text-green-700 font-bold">
                      2
                    </div>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-2">Post a Job</h4>
                    <p className="text-gray-600">
                      Create detailed job listings with specific requirements, responsibilities, and preferences. Our AI will use this information to find the best matches for your needs.
                    </p>
                  </div>
                </li>

                <li className="flex">
                  <div className="mr-4">
                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-green-100 text-green-700 font-bold">
                      3
                    </div>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-2">Review Candidates</h4>
                    <p className="text-gray-600">
                      Browse applications or search for candidates directly. Our AI-powered ranking system helps you quickly identify the most suitable candidates for your position.
                    </p>
                  </div>
                </li>

                <li className="flex">
                  <div className="mr-4">
                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-green-100 text-green-700 font-bold">
                      4
                    </div>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-2">Interview & Hire</h4>
                    <p className="text-gray-600">
                      Schedule interviews with promising candidates through our platform, communicate directly, and hire the perfect virtual assistant for your business needs.
                    </p>
                  </div>
                </li>
              </ol>

              <div className="mt-8">
                <Link to="/auth/register?role=employer">
                  <Button variant="primary" size="lg" className="w-full">
                    Hire a VA
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* AI Matching Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center gap-12">
            <div className="md:w-1/2">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">AI-Powered Matching</h2>
              <p className="text-lg text-gray-700 mb-6">
                Our advanced AI algorithm analyzes multiple factors to create the perfect match between VAs and employers:
              </p>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <div className="mr-3 mt-1">
                    <Icon name="Check" size={20} className="text-primary" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">Skill Compatibility</h4>
                    <p className="text-gray-600">We match based on verified skills and experience levels</p>
                  </div>
                </li>
                <li className="flex items-start">
                  <div className="mr-3 mt-1">
                    <Icon name="Check" size={20} className="text-primary" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">Work Style & Preferences</h4>
                    <p className="text-gray-600">We consider communication styles, availability, and work preferences</p>
                  </div>
                </li>
                <li className="flex items-start">
                  <div className="mr-3 mt-1">
                    <Icon name="Check" size={20} className="text-primary" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">Industry Experience</h4>
                    <p className="text-gray-600">We prioritize VAs with relevant industry experience</p>
                  </div>
                </li>
                <li className="flex items-start">
                  <div className="mr-3 mt-1">
                    <Icon name="Check" size={20} className="text-primary" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">Budget Alignment</h4>
                    <p className="text-gray-600">We match based on budget compatibility and rate expectations</p>
                  </div>
                </li>
              </ul>
            </div>
            <div className="md:w-1/2">
              <div className="bg-white rounded-xl shadow-lg p-8 border border-gray-200">
                <div className="flex items-center justify-center mb-6">
                  <div className="w-16 h-16 bg-primary-50 rounded-full flex items-center justify-center">
                    <Icon name="Zap" size={32} className="text-primary" />
                  </div>
                </div>
                <h3 className="text-xl font-semibold text-center text-gray-900 mb-4">Match Score</h3>
                <div className="bg-gray-100 rounded-full h-4 mb-4">
                  <div className="bg-primary h-4 rounded-full" style={{ width: '85%' }}></div>
                </div>
                <div className="flex justify-between text-sm text-gray-600 mb-6">
                  <span>0%</span>
                  <span className="font-medium text-primary">85%</span>
                  <span>100%</span>
                </div>
                <div className="space-y-4">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Skills Match</span>
                    <span className="font-medium">92%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Experience Match</span>
                    <span className="font-medium">85%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Budget Compatibility</span>
                    <span className="font-medium">78%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Work Style Compatibility</span>
                    <span className="font-medium">90%</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Skills Verification Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Skills Verification</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our comprehensive skills assessment system ensures that employers can trust the expertise of our virtual assistants
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center mb-4">
                <Icon name="FileText" size={24} className="text-blue-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Knowledge Tests</h3>
              <p className="text-gray-600">
                VAs complete comprehensive quizzes and assessments to verify their knowledge in specific areas, from administrative tasks to technical skills.
              </p>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="w-12 h-12 bg-green-50 rounded-lg flex items-center justify-center mb-4">
                <Icon name="CheckSquare" size={24} className="text-green-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Practical Assessments</h3>
              <p className="text-gray-600">
                Hands-on tasks simulate real-world scenarios, allowing VAs to demonstrate their practical abilities in their areas of expertise.
              </p>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="w-12 h-12 bg-purple-50 rounded-lg flex items-center justify-center mb-4">
                <Icon name="Award" size={24} className="text-purple-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Skill Badges</h3>
              <p className="text-gray-600">
                Verified skills are prominently displayed on VA profiles with badges, making it easy for employers to identify qualified candidates.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-primary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-white mb-6">Ready to Get Started?</h2>
            <p className="text-xl text-white opacity-90 max-w-3xl mx-auto mb-8">
              Join thousands of businesses and virtual assistants already growing with VA Sprouts
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/login-register?tab=register&role=employer">
                <Button 
                  variant="white" 
                  size="lg" 
                  className="px-8"
                >
                  Hire a VA
                </Button>
              </Link>
              <Link to="/login-register?tab=register&role=va">
                <Button 
                  variant="outline-white" 
                  size="lg" 
                  className="px-8"
                >
                  Join as a VA
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
      
      {/* Footer */}
      <Footer />
    </>
  );
};

export default HowItWorksPage;
